import { apiResourceUrl } from "@ploy-lib/core";
import paramsToString, { BaseResource } from "./BaseResource";
import { Entity, SimpleResource } from "@rest-hooks/rest";

class ObjectVehicleDetailsEntity extends Entity {
	readonly carMake?: string = undefined;
	readonly carModel?: string = undefined;
	readonly carEngine?: string = undefined;
	readonly yearModel?: string = undefined;
	readonly regNr?: string = undefined;

	pk(parent?: any, key?: string) {
		return "objectVehicleDetails";
	}
}

class CommonFinancialDetailsEntity extends Entity {
	readonly listPrice?: number = undefined;
	readonly carPrice?: number = undefined;
	readonly equity?: number = undefined;
	readonly equityPercent?: number = undefined;
	readonly loanAmount?: number = undefined;
	readonly loanType?: string = undefined;
	readonly termsTotal?: number = undefined;
	readonly termLengthType?: string = undefined;
	readonly totalMonths?: number = undefined;
	readonly interestOnlyMonths?: number = undefined;

	pk(parent?: any, key?: string) {
		return "commonFinancialDetails";
	}
}

class RatesAndFeesDetailsEntity extends Entity {
	readonly netTotalTermAmount?: number = undefined;
	readonly nominalInterestRate?: number = undefined;
	readonly actualInterestRate?: number = undefined;
	readonly establishmentFee?: number = undefined;
	readonly documentFee?: number = undefined;
	readonly termFee?: number = undefined;

	pk(parent?: any, key?: string) {
		return "ratesAndFeesDetails";
	}
}

class CustomerDetailsEntity extends Entity {
	readonly commentFromCustomer?: string = undefined;
	readonly householdStatus?: string = undefined;
	readonly shouldSplitLoan?: string = undefined;

	pk(parent?: any, key?: string) {
		return "customerDetails";
	}
}

class PropertyDetailsEntity extends Entity {
	readonly estimatedValue?: number = undefined;
	readonly jointDebt?: number = undefined;
	readonly useType?: string = undefined;
	readonly applicationPropertyId?: number = undefined;
	readonly areaScore?: number = undefined;
	readonly municipalityScore?: number = undefined;

	pk(parent?: any, key?: string) {
		return this.applicationPropertyId?.toString() ?? "";
	}
}

class DebtDetailsEntity extends Entity {
	readonly loanType?: string = undefined;
	readonly amount?: number = undefined;
	readonly customNumber?: number = undefined;
	readonly applicationDebtId?: number = undefined;

	pk(parent?: any, key?: string) {
		return this.applicationDebtId?.toString() ?? "";
	}
}

class LoanTypeEntity extends Entity {
	readonly key?: string = undefined;
	readonly value?: string = undefined;

	pk(parent?: any, key?: string) {
		return this.key;
	}
}

export interface FinancingInformationData {
	readonly applicationId?: number;

	readonly objectType?: string;
	readonly carType?: string;

	// For use of ObjectDTO
	readonly carObjectData?: ObjectVehicleDetailsEntity;

	// Common financial details
	readonly commonFinancialData?: CommonFinancialDetailsEntity;

	// Rates and fees
	readonly ratesAndFeesData?: RatesAndFeesDetailsEntity;

	readonly customerData?: CustomerDetailsEntity;

	readonly propertyData?: PropertyDetailsEntity[];

	readonly debtData?: DebtDetailsEntity[];

	readonly loanTypeData?: LoanTypeEntity[];
}

export class FinancingInformationResource
	extends BaseResource
	implements FinancingInformationData
{
	readonly applicationId?: number = undefined;

	readonly objectType?: string = undefined;
	readonly carType?: string = undefined;

	readonly carObjectData?: ObjectVehicleDetailsEntity = undefined;
	readonly commonFinancialData?: CommonFinancialDetailsEntity = undefined;
	readonly ratesAndFeesData?: RatesAndFeesDetailsEntity = undefined;
	readonly customerData?: CustomerDetailsEntity = undefined;
	readonly propertyData?: PropertyDetailsEntity[] = undefined;
	readonly debtData?: DebtDetailsEntity[] = undefined;
	readonly loanTypeData?: LoanTypeEntity[] = undefined;

	pk(parent?: any, key?: string): string | undefined {
		return `${this.applicationId?.toString()}`;
	}

	static url<T extends typeof SimpleResource>(
		this: T,
		urlParams: Readonly<Record<string, any>>
	) {
		const { applicationId, ...searchParams } = urlParams;

		const url = super.url(urlParams);

		return `${url}${paramsToString(searchParams)}`;
	}

	static schema = {
		carObjectData: ObjectVehicleDetailsEntity,
		commonFinancialData: CommonFinancialDetailsEntity,
		ratesAndFeesData: RatesAndFeesDetailsEntity,
		customerData: CustomerDetailsEntity,
		propertyData: [PropertyDetailsEntity],
		debtData: [DebtDetailsEntity],
		loanTypeData: [LoanTypeEntity]
	};

	static urlRoot = apiResourceUrl("financing-information");
}
